// App.js
import { useEffect, useState } from 'react';
import { Snackbar } from '@mui/material';
import AppMain from './AppMain';
import MapMain from './MapMain';
import UserSessionInfo from './com/common/UserSessionInfo';
import { Waiting, ErrorBox } from './com/message/Waiting';
import { CvoMsg, AppObject, AppWord, MapValue } from './model/AppConst';
import {mapLinkInfoRepo, mmlog, userInfoRepo} from './model/CvoModel';
import {useLazyQuery, useReactiveVar} from '@apollo/client';
import {GET_SESSION_DATA, MAP_LINK_LOGIN} from './com/session/SessionGql';
import ValueUtil from './model/ValueUtil';

function parseUrlIfMapLink() {
    if(window.location.href.indexOf("ci=") > 10 && window.location.href.indexOf("ed=") > 10) {
        // window.location.search is a JavaScript property that returns the
        // query string portion of the current URL, including the leading question mark (?).
        const urlParams = new URLSearchParams(window.location.search);
        if(urlParams.get("ci") !== null && urlParams.get("ed") !== null) {
            const custId = urlParams.get("ci");
            const encodedData = urlParams.get("ed");
            const aes = urlParams.get("em")==='B' ? false : true; // A[ES128], B[Base64]
            const mobile = urlParams.get("m") === 'Y';
            return {custId, encodedData, aes, mobile};
        }
    }
    else if(window.location.href.indexOf("job=") > 10) {
        // Popup으로 지도 띄울 때 정보 전달용. mapLinkInfoRepo 설정용 데이터를 그대로 반환.
        // url 형태: go.etrace.co.kr/?job=veh|route|position&vehId=1234&from=YYYYMMDDHHmmss&till=YYYYMMDDHHmmss&mapLevel=5&lat=37.5&lon=126.9#view=map
        const urlParams = new URLSearchParams(window.location.search);
        const job = urlParams.get("job");
        const from = urlParams.get("from");
        const till = urlParams.get("till");

        return {
            show: job,
            showDataBox: true,
            vehId: urlParams.get("vehId") ? parseInt(urlParams.get("vehId")) : null,
            from: (from ? ValueUtil.repairYmdHmsText(from) : null),
            till: (till ? ValueUtil.repairYmdHmsText(till) : null),
            mapLevel: urlParams.get("mapLevel") ? parseInt(urlParams.get("mapLevel")) : MapValue.Level.ZOOM_INIT,
            lat: urlParams.get("lat") ? parseFloat(urlParams.get("lat")) : null,
            lon: urlParams.get("lon") ? parseFloat(urlParams.get("lon")) : null,
        };
    }
    return null;
}

function App() {
    const [mapLinkError, setMapLinkError] = useState(null);
    const [checkError, setCheckError] = useState(null);
    const sessionInfo = useReactiveVar(userInfoRepo);
    const [isMapApp, setIsMapApp] = useState(window.location.href.indexOf("view=map") > 1);
    const [snackBars, setSnackBars] = useState([]);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [messageInfo, setMessageInfo] = useState(undefined);
    const [checkSession, responseCheck] = useLazyQuery(GET_SESSION_DATA,
        {
            ...AppObject.NoCachedFetch,
            onCompleted: (data, option) => {onCompletionCheckSession(data, option);},
            onError: (error) => {
                setCheckError(error);
            }
        }
    );
    const [mapLinkLogin, responseMapLinkLogin] = useLazyQuery(MAP_LINK_LOGIN, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteMapLinkLogin(data, option);},
        onError: (error) => {
            setMapLinkError("지도 링크 로그인에 실패했습니다. 잠시후 다시 시도해 주세요.");
            sessionInfo.ifLocal(()=>console.error("Map link login error:", error));
        }
    });

    // 지도를 띄우는지 확인해서 설정할 필요.
    useEffect(() => {
        document.title = '차량관제-eTrace';
        mmlog("App started.");
        /*
        - 외부업체에서 지도링크로 접근하는 경우 입력된내용을 바탕으로 서버에 별도 세션작업을 요청해야 함. (ci,ed,em,m)
        - ci=custId, ed=encoded_data, em=encoding_method(A[ES128], B[Base64]), m=Y/N(is-mobile)
        - 서버에서 세션을 생성하고, 세션정보와 ed에 들어있는 내용을 분석하여 작업할 내용을 함께 반환.
         */
        const mapLinkData = parseUrlIfMapLink();
        if(mapLinkData) {
            if(mapLinkData.custId) {
                mmlog("Map link detected. custId=" + mapLinkData.custId);
                // 서버에 세션 요청을 하고, 받은 세션정보를 저장해야 함.
                mapLinkLogin({
                    variables: {
                        custId: mapLinkData.custId,
                        encodedData: mapLinkData.encodedData,
                        aes: mapLinkData.aes,
                        mobile: mapLinkData.mobile
                    }
                });
            }
            else if(mapLinkData.show) {
                sessionInfo.ifLocal(()=>console.log("Map link view job detected:", mapLinkData));
                mapLinkInfoRepo(mapLinkData);
                checkSession();
            }
        }
        else checkSession(); // mapLink일 경우에는 세션 체크할 필요 없음.
    }, []);

    useEffect(() => {
        const onBubbleSnack = (event) => {
            const snacks = [...snackBars];
            const {detail} = event;
            snacks.push({...detail});
            setSnackBars(snacks);
        };

        document.addEventListener(AppWord.SNACK_EVENT, onBubbleSnack);
        return () => {document.removeEventListener(AppWord.SNACK_EVENT, onBubbleSnack);};
    }, []);

    const onCompletionCheckSession = (data, option) => {
        if(data.getSessionData) {
            const isSignedIn = data.getSessionData.user.msg===CvoMsg.OK;
            /* userInfoRepo({
                ...data.getSessionData,
                signedIn: isSignedIn,
                sessionChecked: true
            }); */
            userInfoRepo(new UserSessionInfo(true, isSignedIn, data.getSessionData));
        }
    };

    const onCompleteMapLinkLogin = (data, option) => {
        if(data.mapLinkLogin) {
            const {msg, mapShowJob, sessionData} = data.mapLinkLogin;
            if(msg === CvoMsg.OK) {
                // userInfoRepo(new UserSessionInfo(true, true, data.login));
                userInfoRepo(new UserSessionInfo(true, true, sessionData));
                // Repair if ...
                if(mapShowJob.show === MapValue.Show.route) {
                    mapShowJob.from = ValueUtil.repairYmdHmsText(mapShowJob.from);
                    mapShowJob.till = ValueUtil.repairYmdHmsText(mapShowJob.till);
                }
                if(mapShowJob.mapLevel !== undefined && mapShowJob.mapLevel !== null) {
                    mapShowJob.mapLevel += MapValue.Level.ZOOM_LEVEL_DELTA;
                }
                else mapShowJob.mapLevel = MapValue.Level.ZOOM_INIT;
                mapLinkInfoRepo(mapShowJob);
                // mapShowJob 정보를 사용해서 다음에 할 일들을 설정한다.
                // mapShowJob.show: veh | route | [position:default]
                /*
                veh: 특정 차량의 위치로 지도 중심을 이동하게 함. (vehId)
                route: 특정 차량의 경로를 보여주게 함. (vehId, from, till). from, till: YYYYMMDD HHmmss.
                position: 모든 차량의 위치를 보여주게 함. 특별히 할 일 없음.
                */
                setIsMapApp(true);
            }
            else {
                // 로그인 실패시 화면에 크게 보여준다.
                setMapLinkError(msg);
            }
        }
        else {
            setMapLinkError("지도 링크 로그인에 실패했습니다. 서버에서 응답이 없습니다.");
        }
    };

    useEffect(() => {
        if (snackBars.length && !openSnackbar) {
            // Set a new snack when we don't have an active one
            setMessageInfo(snackBars.shift());
            setSnackBars([...snackBars]);
            setOpenSnackbar(true);
        }
        else if (snackBars.length && messageInfo && openSnackbar) {
            // Close an active snack when a new one is added
            setOpenSnackbar(false);
        }
    }, [snackBars, messageInfo, openSnackbar]);

    const closeSnackBar = () => {
        setOpenSnackbar(false);
    };

    if(mapLinkError) {
        return <ErrorBox messages={[
            "지도 링크로 접근하는 과정에서 오류가 발생했습니다.",
            mapLinkError
        ]} />;
    }

    // if(loading) return <Waiting message="로그인 상태 점검중입니다."/>;
    if(checkError)
        return <ErrorBox messages={[
            "로그인 상태 점검중 오류가 발생했습니다.",
            "잠시 후 다시 시도해 주세요."]}
        />;

    if(sessionInfo.sessionChecked) {
        return (
            <>
            {
                isMapApp ? <MapMain /> : <AppMain />
            }
            <Snackbar
                open={openSnackbar}
                autoHideDuration={messageInfo && messageInfo.duration ? messageInfo.duration : 5000}
                onClose={closeSnackBar}
                message={messageInfo ? messageInfo.message : 'Undefined message'}
                anchorOrigin={messageInfo && messageInfo.anchorOrigin ? messageInfo.anchorOrigin : {vertical:'bottom', horizontal:'center'}}
                sx={messageInfo ? messageInfo.sx : undefined}
            />
            </>
        );
    }
    else {
        return(<Waiting message="* 로그인 상태 점검중입니다 *"/>);
    }
}

export default App;

/*
poi_group 관련 Memo
==================
poi_group은 동원홈푸드에서 사용하던 것이었음.
동원홈푸드가 서비스 탈퇴했기 때문에 현재는 사용하지 않음.
user_poi_group_map, poi_group_map 테이블도 현재는 사용하지 않음.

*/